import i18n from "@/plugins/vue-i18n";
import { fullTextSearch } from "@/api/products";
import NotifyService from "@/services/notify.service";
import { fetchCart, saveCart } from "@/services/cart.service";
import { showNotifyError } from "@/utils/graphqlErrorNotify";
import {
  getCartLineId,
  mapCartBalance,
  mapCartResponseToPreviewItems,
  mapCatalogProductToCartModalItem,
} from "@/utils/cartItems.mapper";

export default {
  namespaced: true,
  state: {
    status: {
      free: {
        label: i18n.t("product.status.available"),
        color: "green",
        value: "free",
      },
      reserved: {
        label: i18n.t("product.status.reserved"),
        color: "yellow",
        value: "reserved",
      },
      notAvailable: {
        label: i18n.t("product.status.notAvailable"),
        color: "red",
        value: "notAvailable",
      },
      expected: {
        label: i18n.t("product.status.expected"),
        color: "violet",
        value: "expected",
      },
      preOrder: {
        label: i18n.t("product.status.preOrder"),
        color: "blue",
        value: "preOrder",
      },
    },
    cartProducts: [],
    pendingAddToCartNotify: 0,
    /** Product id (last add) while server save is in progress; cleared after saveCart completes */
    addingToCartProductId: null,
    /** True while adding a bundle kit; cleared after saveCart completes */
    isAddingBundleToCart: false,
    isCartModalOpen: false,
    cartModalSourceProduct: null,
    cartModalSourceProducts: null,
    lastAddedCartItem: null,
    cartPreviewItems: [],
    cartBalance: {},
    isCartSaving: false,
    skipCartSync: false,
    searchForm: {
      category: {},
      search: "",
      contract: "",
      name: "",
      status: "",
    },
    filtersForm: {
      name: "",
      status: "",
    },
    currentPaymentMethod: "",
    tags: [
      {
        type: "discount",
        value: i18n.t("product.promotions.discount"),
        color: "red",
        symbol: "%",
      },
      {
        type: "bonus",
        value: i18n.t("product.promotions.bonus"),
        color: "green",
        symbol: "%",
      },
      {
        type: "bundleDiscount",
        value: i18n.t("product.promotions.bundle_discount"),
        color: "orange",
      },
      {
        type: "retroBonusPercent",
        value: i18n.t("product.promotions.retro_bonus"),
        color: "yellow",
        symbol: "%",
      },
      {
        type: "retroBonusAmount",
        value: i18n.t("product.promotions.retro_bonus"),
        color: "yellow",
        symbol: "$",
      },
      {
        type: "wholesalePrices",
        value: i18n.t("product.promotions.wholesalePrices"),
        color: "blue",
      },
      {
        type: "preOrder",
        value: i18n.t("product.status.preOrder"),
        color: "blue",
      },
    ],
  },
  getters: {
    getStatus(state) {
      return (status, date) => {
        if (state.status[status]) {
          if (state.status[status].value === "expected") {
            const { label, color, value } = state.status[status];

            return {
              label: `${label} ${date}`,
              color,
              value,
            };
          }

          return state.status[status];
        }
      };
    },

    getStatuses(state) {
      return Object.values(state.status);
    },

    getCartProducts(state) {
      const { cartProducts } = state;

      cartProducts.forEach((item) => {
        if (!item.quantity) item.quantity = 1;
      });

      return cartProducts;
    },

    getCategories(state) {
      return state.searchForm.category;
    },

    getFilterForm(state) {
      return state.filtersForm;
    },

    getPaymentMethod(state) {
      return state.currentPaymentMethod;
    },

    getTags(state) {
      return state.tags;
    },

    getCartPreviewItems(state) {
      return state.cartPreviewItems;
    },

    getCartBalance(state) {
      return state.cartBalance;
    },
  },
  mutations: {
    SET_CART_PRODUCTS(state, cart) {
      state.cartProducts = cart;
    },

    ADD_PRODUCT_TO_CART(state, product) {
      const { cartProducts } = state;
      const lineId = getCartLineId(product);
      const index = cartProducts.findIndex((item) => getCartLineId(item) === lineId);

      if (index !== -1 && !product.supplyId) {
        const stateProduct = cartProducts[index];

        stateProduct["quantity"] += product["quantity"];

        cartProducts.splice(index, 1, stateProduct);
      } else {
        cartProducts.push({
          ...product,
          cartLineId: lineId,
        });
      }

      state.pendingAddToCartNotify += 1;

      if (!state.isAddingBundleToCart) {
        state.addingToCartProductId = product.id;
      }
    },

    DELETE_PRODUCT_FROM_CART(state, lineId) {
      const { cartProducts } = state;
      const index = cartProducts.findIndex((item) => getCartLineId(item) === String(lineId));

      if (index === -1) {
        return;
      }

      cartProducts.splice(index, 1);

      if (!cartProducts.length) state.cartProducts = [];
    },

    SET_PRODUCT_QUANTITY(state, [lineId, newProductQuantity]) {
      const { cartProducts } = state;
      const index = cartProducts.findIndex((item) => getCartLineId(item) === String(lineId));

      if (index === -1) {
        return;
      }

      const product = cartProducts[index];

      product["quantity"] = newProductQuantity;
      cartProducts.splice(index, 1, product);
    },

    SET_CATEGORY(state, category) {
      state.searchForm.category = category;
    },

    SET_SEARCH(state, search) {
      state.searchForm.search = search;
    },

    SET_CONTACT(state, contract) {
      state.searchForm.contract = contract;
    },

    SET_FILTERS_FORM(state, form) {
      state.filtersForm = form;
    },

    SET_PAYMENT_METHOD(state, method) {
      state.currentPaymentMethod = method;
    },

    CLEAR_PENDING_ADD_TO_CART_NOTIFY(state) {
      state.pendingAddToCartNotify = 0;
    },

    SET_ADDING_BUNDLE_TO_CART(state, value) {
      state.isAddingBundleToCart = Boolean(value);
    },

    CLEAR_ADD_TO_CART_LOADING(state) {
      state.addingToCartProductId = null;
      state.isAddingBundleToCart = false;
    },

    SET_CART_MODAL_OPEN(state, value) {
      state.isCartModalOpen = Boolean(value);
    },

    CLEAR_CART_MODAL_DATA(state) {
      state.lastAddedCartItem = null;
      state.cartModalSourceProduct = null;
      state.cartModalSourceProducts = null;
    },

    SET_CART_MODAL_SOURCE_PRODUCT(state, product) {
      state.cartModalSourceProduct = product;
    },

    SET_CART_MODAL_SOURCE_PRODUCTS(state, products) {
      state.cartModalSourceProducts = products;
    },

    SET_LAST_ADDED_CART_ITEM(state, item) {
      state.lastAddedCartItem = item;
    },

    SET_CART_PREVIEW_ITEMS(state, items) {
      state.cartPreviewItems = items;
    },

    SET_CART_BALANCE(state, balance) {
      state.cartBalance = balance;
    },

    SET_CART_SAVING(state, value) {
      state.isCartSaving = Boolean(value);
    },

    SET_SKIP_CART_SYNC(state, value) {
      state.skipCartSync = Boolean(value);
    },

    CLEAR_CATEGORY(state) {
      state.searchForm.category = {};
    },
  },

  actions: {
    async getProductsDataByFullTextSearch(context, searchData) {
      return { products: await fullTextSearch(searchData) };
    },

    async fetchCartPreview({ commit, getters }, { skipSync = false } = {}) {
      const cart = await fetchCart();

      if (!cart) {
        return;
      }

      const { sum, paymentMethod } = cart;

      if (!getters.getPaymentMethod) {
        commit("SET_PAYMENT_METHOD", paymentMethod);
      }

      const paymentMethodToUse = getters.getPaymentMethod || paymentMethod;
      const mappedItems = mapCartResponseToPreviewItems(cart, paymentMethodToUse);

      if (skipSync) {
        commit("SET_SKIP_CART_SYNC", true);
      }

      commit("SET_CART_PREVIEW_ITEMS", mappedItems);
      commit("SET_CART_BALANCE", mapCartBalance(sum));
      commit("SET_CART_PRODUCTS", mappedItems);
    },

    async persistCart({ commit, dispatch, state, getters }, { showNotify = false } = {}) {
      if (state.isCartSaving) {
        return;
      }

      try {
        commit("SET_CART_SAVING", true);

        const shouldNotify = showNotify || state.pendingAddToCartNotify > 0;
        const savedCart = await saveCart(getters.getCartProducts);
        const mappedItems = mapCartResponseToPreviewItems(savedCart, state.currentPaymentMethod);

        commit("SET_CART_PREVIEW_ITEMS", mappedItems);
        commit("SET_SKIP_CART_SYNC", true);
        commit("SET_CART_PRODUCTS", mappedItems);
        commit("SET_CART_BALANCE", mapCartBalance(savedCart?.sum));

        if (state.pendingAddToCartNotify > 0) {
          commit("CLEAR_PENDING_ADD_TO_CART_NOTIFY");
        }

        if (shouldNotify) {
          NotifyService.success("addToCart");
        }

        return savedCart;
      } catch (error) {
        commit("CLEAR_PENDING_ADD_TO_CART_NOTIFY");

        try {
          await dispatch("fetchCartPreview", { skipSync: true });
        } catch {
          commit("SET_SKIP_CART_SYNC", true);
          commit("SET_CART_PRODUCTS", []);
          commit("SET_CART_PREVIEW_ITEMS", []);
        }

        showNotifyError(error);
        throw error;
      } finally {
        commit("CLEAR_ADD_TO_CART_LOADING");
        commit("SET_CART_SAVING", false);
      }
    },

    openAddToCartModal({ commit }, payload) {
      const products = payload?.products ?? null;
      const product = payload?.product ?? (products ? null : payload);
      const quantity = payload?.quantity ?? 1;
      const displayProduct = product ?? products?.[products.length - 1];
      const previewItem = mapCatalogProductToCartModalItem(
        displayProduct,
        quantity,
        payload?.amount ?? null
      );

      commit("SET_CART_MODAL_SOURCE_PRODUCTS", products);
      commit("SET_CART_MODAL_SOURCE_PRODUCT", products ? null : product);
      commit("SET_LAST_ADDED_CART_ITEM", previewItem);
      commit("SET_CART_MODAL_OPEN", true);
    },

    async confirmAddToCartFromModal({ commit, dispatch, state }, options = {}) {
      const bundleProducts = state.cartModalSourceProducts;
      const sourceProduct = state.cartModalSourceProduct;
      const cartOptions = typeof options === "number" ? { quantity: options } : options;
      const { quantity = 1, supplyId, reservationType, packCount } = cartOptions;

      if (bundleProducts?.length) {
        await dispatch("addProductsToCart", {
          products: bundleProducts.map((product) => ({ ...product, quantity: 1 })),
          showNotify: true,
          skipPreview: true,
        });
      } else if (sourceProduct) {
        const product = { ...sourceProduct, quantity };

        if (supplyId) {
          product.supplyId = supplyId;
          product.reservationType = reservationType;
          product.packCount = packCount;
        }

        await dispatch("addToCart", {
          product,
          showNotify: true,
          skipPreview: true,
        });
      } else {
        return;
      }

      commit("SET_CART_MODAL_OPEN", false);
    },

    async addToCart({ commit, dispatch }, { product, showNotify = true, skipPreview = false }) {
      if (!skipPreview) {
        return dispatch("openAddToCartModal", {
          product,
          quantity: product?.quantity ?? 1,
        });
      }

      commit("SET_SKIP_CART_SYNC", true);
      commit("ADD_PRODUCT_TO_CART", product);

      return dispatch("persistCart", { showNotify });
    },

    async addProductsToCart(
      { commit, dispatch },
      { products, showNotify = true, skipPreview = false }
    ) {
      if (!skipPreview) {
        const lastProduct = products[products.length - 1];

        return dispatch("openAddToCartModal", {
          products,
          product: lastProduct,
          quantity: 1,
        });
      }

      commit("SET_ADDING_BUNDLE_TO_CART", true);
      commit("SET_SKIP_CART_SYNC", true);

      products.forEach((product) => {
        commit("ADD_PRODUCT_TO_CART", product);
      });

      return dispatch("persistCart", { showNotify });
    },
  },
};
